/* @jsx h */
import { computed, defineComponent, h } from "vue";
import type { TableCredit } from "@lemmonhq/types";
import { formatPrice } from "@lemmonhq/orders";
import { cn, useLocalization } from "@lemmonhq/uikit";
import { TABLE_CREDIT_LOW_BALANCE } from "./tableCredit";

export type TableCreditBarProps = {
  credit: TableCredit;
  /** What the table already owes on its bill; the credit pays it at checkout. */
  owed?: number;
  /** Total of the cart about to be sent, 0 when just showing the balance. */
  orderTotal: number;
  currency: string;
  /** Shows a [+] button on the bar; omitted when the caller has its own. */
  onAddCredit?: () => void;
  onShowHistory?: () => void;
  /** The restaurant lets the table order beyond its credit. */
  allowBeyondCredit?: boolean;
};

/**
 * One row with what a credited table can still spend (credit minus what it
 * already owes) and what this order leaves. For an order beyond that, it says
 * either that the rest is paid at checkout (allowBeyondCredit) or how much
 * must be topped up first (docs/plans/table_credit/pos_checkout_credit.md).
 */
export const TableCreditBar = defineComponent<TableCreditBarProps>({
  name: "TableCreditBar",
  props: [
    "credit",
    "orderTotal",
    "currency",
    "onAddCredit",
    "onShowHistory",
    "allowBeyondCredit",
    "owed",
  ],
  setup(props) {
    const { t } = useLocalization();

    const available = computed(() =>
      Math.max(props.credit.balance - (props.owed || 0), 0),
    );
    const left = computed(() => available.value - props.orderTotal);
    const fits = computed(() => left.value >= -0.005);
    const low = computed(
      () => fits.value && left.value < TABLE_CREDIT_LOW_BALANCE,
    );
    // An order that fits gets the three-line breakdown.
    const breakdown = computed(() => props.orderTotal > 0 && fits.value);
    const money = (value: number) =>
      formatPrice(value, true, 2, props.currency);

    return () => (
      <div
        class={cn(
          "flex items-center gap-1 rounded-lg border py-1 pl-2 pr-1 text-sm",
          !fits.value
            ? props.allowBeyondCredit
              ? "border-warning/50 bg-warning/10"
              : "border-error/50 bg-error/10"
            : low.value
              ? "border-warning/40 bg-warning/5"
              : "border-base-300 bg-base-200",
        )}
      >
        <button
          type="button"
          class="flex min-w-0 flex-1 items-center gap-2 text-left"
          disabled={!props.onShowHistory}
          onClick={() => props.onShowHistory?.()}
        >
          <span
            class={cn(
              "material-symbols-outlined text-base text-base-content/55",
              breakdown.value && "self-start",
            )}
          >
            account_balance_wallet
          </span>
          <span class="flex min-w-0 flex-1 flex-col leading-tight">
            {breakdown.value ? (
              // Receipt-style: balance, minus this order, and what is left
              // under a rule, one line each so nothing truncates on a phone.
              <span class="flex flex-col gap-1 py-1 pr-1 tabular-nums">
                <span class="flex items-baseline justify-between gap-3">
                  <span class="truncate text-base-content/60">
                    {t("pos.table_credit.title")}
                  </span>
                  <span class="shrink-0 font-medium">
                    {money(available.value)}
                  </span>
                </span>
                <span class="flex items-baseline justify-between gap-3">
                  <span class="truncate text-base-content/60">
                    {t("general.words.total")}
                  </span>
                  <span class="shrink-0 font-medium">
                    −{money(props.orderTotal)}
                  </span>
                </span>
                <span class="mt-0.5 flex items-baseline justify-between gap-3 border-t border-dashed border-base-content/20 pt-1.5">
                  <span class="truncate font-semibold">
                    {t("pos.table_credit.left_after_order")}
                  </span>
                  <span
                    class={cn(
                      "shrink-0 text-base font-bold",
                      low.value ? "text-warning" : "text-accent",
                    )}
                  >
                    {money(left.value)}
                  </span>
                </span>
              </span>
            ) : (
              <span class="truncate tabular-nums">
                <span class="text-base-content/60">
                  {t("pos.table_credit.title")}
                </span>{" "}
                <span class="font-semibold">{money(available.value)}</span>
              </span>
            )}
            {props.orderTotal > 0 && !fits.value ? (
              <span
                class={cn(
                  "truncate text-xs font-medium",
                  props.allowBeyondCredit ? "text-warning" : "text-error",
                )}
              >
                {(props.allowBeyondCredit
                  ? t("pos.table_credit.beyond_credit")
                  : t("pos.table_credit.missing_top_up")
                ).replace("{0}", money(-left.value))}
              </span>
            ) : null}
          </span>
        </button>
        {props.onAddCredit ? (
          <button
            type="button"
            class="btn btn-ghost btn-sm btn-square"
            aria-label={t("pos.table_credit.add_credit")}
            title={t("pos.table_credit.add_credit")}
            onClick={() => props.onAddCredit?.()}
          >
            <span class="material-symbols-outlined text-lg">add_card</span>
          </button>
        ) : null}
      </div>
    );
  },
});
